import { useCallback } from 'react'
import * as React from 'react'
import type moment from 'moment'
import { FormattedMessage, useIntl } from 'react-intl'
import BodyText from 'LuxKit/Typography/BodyText'
import LineRefundIcon from 'LuxKit/Icons/line/LineRefundIcon'
import Group from 'components/utils/Group'
import { INTL_DAY_MONTH_NAME_SHORT } from 'constants/dateFormats'
import { useIntlDateFormatter } from 'lib/datetime/dateUtils'
import useRebrandSuccessDarkColour from 'hooks/useRebrandSuccessDarkColour'

interface Props {
  compact?: boolean
  deadline?: moment.Moment
  isFlashUSP?: boolean
  showHotelOnly?: boolean
}

function OfferPropertyCashRefundPolicy({ compact, deadline, isFlashUSP, showHotelOnly = false }: Props) {
  const intl = useIntl()
  const intlDateFormatter = useIntlDateFormatter()
  const uspColour = useRebrandSuccessDarkColour()

  const highlightText = useCallback((chunks: React.ReactNode) => (
    <BodyText variant={compact ? 'small' : 'medium'} weight="bold" as="span" colour={uspColour}>{chunks}</BodyText>
  ), [compact, uspColour])

  const date = deadline ? intlDateFormatter(deadline, INTL_DAY_MONTH_NAME_SHORT) : undefined

  const content = <BodyText variant={compact ? 'small' : 'medium'} as="span">
    {date && !showHotelOnly && <FormattedMessage
      id="Pkc_4CaPOwrir9o0vVjy"
      defaultMessage="<highlight>Fully refundable</highlight> until {date}"
      description="Fully refundable cancellation option with its cash refund deadline date for standalone hotel bookings"
      values={{ highlight: highlightText, date }}
    />}
    {date && showHotelOnly && <FormattedMessage
      id="Ee4cN2mKoFSd1RlE7GCh"
      defaultMessage="<highlight>Fully refundable</highlight> until {date} (Hotel only)"
      description="Fully refundable cancellation option with its cash refund deadline date for hotel bookings"
      values={{ highlight: highlightText, date }}
    />}
    {!date && !showHotelOnly && <FormattedMessage
      id="pt7rQalRjMN0SegN_inj"
      defaultMessage="<highlight>Fully refundable</highlight> until 10 days before check-in"
      description="Fully refundable cancellation option before a check-in date is chosen for standalone hotel bookings"
      values={{ highlight: highlightText }}
    />}
    {!date && showHotelOnly && <FormattedMessage
      id="eNQPnSF5I_yuzVlxHPy3"
      defaultMessage="<highlight>Fully refundable</highlight> until 10 days before check-in (Hotel only)"
      description="Fully refundable cancellation option before a check-in date is chosen for hotel bookings"
      values={{ highlight: highlightText }}
    />}
  </BodyText>

  if (isFlashUSP) {
    const label = showHotelOnly ? intl.formatMessage({
      id: 'HkdMrDlfBLhlLKCISJ_i',
      defaultMessage: 'Fully refundable (Hotel only)',
      description: 'Short label for the Fully refundable cancellation option in the offer page USP block when another travel product is selected',
    }) : intl.formatMessage({
      id: '-wJfEPstKPLXqrgVDCx1',
      defaultMessage: 'Fully refundable',
      description: 'Short label for the Fully refundable cancellation option in the offer page USP block',
    })
    return <Group direction="horizontal" gap={4} verticalAlign="center">
      <LineRefundIcon colour={uspColour} />
      <BodyText variant="medium" weight="bold" as="span" colour={uspColour}>{label}</BodyText>
    </Group>
  }

  return content
}

export default OfferPropertyCashRefundPolicy
